import { Location } from '@angular/common';
import { FlowchartService } from './../../../services/flowchart.service';
import { DialogDefinition } from './../../../services/overlay.service';
import { ProjectService } from './../../../services/project.service';
import { ProjectKpiService } from './../../../services/project-kpi.service';
import { PublicationService } from './../../../services/publication.service';

import { Subscription, Subject, Observable } from 'rxjs';
import { REDUX_STORE_PROJECT_FLOWCHART } from './../../../shared/redux/app.store';
import { ReduxService } from './../../../services/redux.service';

import { FlowchartStep } from './../../../shared/models/flowchart-step.model';
import { Project } from './../../../interfaces/project';
import { SapiEntity, SapiEntityVersion } from './../../../services/sapi.service';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { AbstractDetailsComponent } from '../../shared/abstract-details.component';
import { Mediaclip } from '../../../interfaces/mediaclip';
import { UNDOABLE_TYPE } from '../../../shared/redux/enums/undoable.type.enum';
import { OverlayService } from '../../../services/overlay.service';
import { Publication } from './../../../interfaces/publication';
import { LibraryProjectDetailsReactComponent } from './library-project-details-react.component';
import { QuickeditService } from 'app/services/quickedit.service';
import { tap } from 'rxjs/operators';

export interface ProjectSapiEntity extends SapiEntity {
	title: string;
	mediatype: string;
}

@Component({
	selector: 'library-project-details',
	templateUrl: './library-project-details.component.html',
	styleUrls: ['./library-project-details.component.scss']
})

export class LibraryProjectDetailsComponent extends AbstractDetailsComponent implements OnInit, OnDestroy {

	@ViewChild('formRef', { static: true }) form: NgForm;
	@ViewChild(LibraryProjectDetailsReactComponent) reactComponent: LibraryProjectDetailsReactComponent;

	entity: ProjectSapiEntity;
	activeTab: string;

	// TODO: Seriously? We really need to check if this can be removed...
	preview: boolean;
	MAYBE_LATER = false;

	mainMediaClip: Mediaclip;
	mainMediaClipSub: Subscription;
	useGenerate = false;
	configuration: any;
	lineStats: any;
	statsLoading = false;
	stateHistory: Project[];
	loadingPerformanceData = false;
	timeZoneName = 'Europe/Berlin';

	private historySubscription: Subscription;
	private getClipSub: Subscription;

	constructor(protected overlayService: OverlayService,
		protected route: ActivatedRoute,
		protected location: Location,
		protected reduxService: ReduxService,
		protected projectService: ProjectService,
		protected quickeditService: QuickeditService,
		private flowchartService: FlowchartService,
		private projectKpiService: ProjectKpiService,
		private publicationService: PublicationService) {
		super(route, location);
		this.entityConfiguration = {
			name: 'project',
			entityType: 'Project',
			parentPath: '/library/projects',
			basePath: '/library/project/',
			versioned: true,
			showVersionMetadata: true,
			useAutoSave: false,
			versionMetadataToSave: {
				bbcomment: ''
			},
			extraEntityParams: {
				includeContent: 'false'
			},
			defaults: {
				subtype: 'versioned'
			}
		};
		this.checklistItems = [];

		this.reduxService.setup(REDUX_STORE_PROJECT_FLOWCHART, UNDOABLE_TYPE.PROJECT_FLOWCHART);
	}

	ngOnInit() {
		super.ngOnInit();
		this.projectService.statsLoading.subscribe((l: any) => {
			this.statsLoading = l;
			this.overlayService.setLoading(l || this.entityLoading);
		});
		let project = (this.entity as Project);

		this.subscriptions.add(this.publicationService.getData().subscribe((pub: Publication) => {
			if (pub && pub.timeZone) {
				this.timeZoneName = pub.timeZone;
			}
		}));

		this.historySubscription = this.reduxService.historyObs.subscribe((historyData: any) => {
			// console.warn('historyData', historyData);
			project = (this.entity as Project);
			if (!project) {
				// console.warn('no project yet');
				return;
			}

			this.stateHistory = [...historyData.past];
			if (historyData && historyData.present) {
				this.stateHistory.push(historyData.present);
				if (project) {
					project.flowchart = historyData.present.data;
				}
				if (historyData.present.type === 'FROM_LOAD') {
					this.form.form.markAsPristine();
				} else {
					this.form.form.markAsDirty();
				}
			}
			this.checkMainMediaClipId();
			if (this.entity && project) {
				(this.entity as Project).flowchart = project.flowchart;
			}

			const p = project;
			if (!p.flowchart) {
				p.flowchart = {};
			}
			if (!p.flowchart.steps) {
				p.flowchart.steps = [];
			}
			Object.keys(p.flowchart.steps).forEach((stepId) => {
				p.flowchart.steps[stepId] = new FlowchartStep(p.flowchart.steps[stepId]);

				if (p.mainMediaClipId && p.flowchart.steps[stepId].isStart) {
					p.flowchart.steps[stepId].update({
						entityId: p.mainMediaClipId
					});
				}
			});
			this.projectService.setProject(p);
		});

		this.mainMediaClipSub = this.projectService.mainMediaClipSubject.subscribe((mediaClip: Mediaclip) => {
			this.mainMediaClip = mediaClip;
		});

		if (this.isNew) {
			this.reduxService.dispatch({ history: 'reset', type: 'CREATE_NEW' });
			this.checkMainMediaClipId();
		}
	}

	hasTimeline(): boolean {
		const p = this.entity as Project;
		if (p && p.timelines && p.timelines[0]) {
			return true;
		}
		return false;
	}

	checkMainMediaClipId() {
		if (this.isNew) {
			this.mainMediaClip = null;
		} else {
			// checks the mainMediaClipId property of the project and updates it if it isn't in sync with the startStep.
			// One way for now, since we don't allow you to change it anywhere else.
			const project = (this.entity as Project);
			if (project && project.flowchart && project.flowchart.steps) {
				Object.keys(project.flowchart.steps).forEach((stepId) => {
					const step = project.flowchart.steps[stepId];
					if (step && step.isStart && typeof step.entityId !== 'undefined' && step.entityId !== project.mainMediaClipId) {
						// console.warn('setting step.entityId to', step.entityId);
						project.mainMediaClipId = step.entityId;
						// if (project.mainMediaClipId) {
						// 	this.getMainMediaClip();
						// } else {
						// 	this.mainMediaClip = null;
						// }
						this.projectService.setMainMediaClipId(step.entityId);
					}
				});
			}
		}
	}

	ngOnDestroy() {
		if (this.getClipSub) {
			this.getClipSub.unsubscribe();
		}
		if (this.historySubscription) {
			this.historySubscription.unsubscribe();
		}
	}

	loadState(state: any) {
		// console.warn('loadState state', state);
		this.reduxService.dispatch({ type: 'GOTOSTATE', state });
	}

	cancel(): Subject<SapiEntity> {
		// note: react data is stored seperately
		if (this.activeTab === 'react') {
			this.reactComponent.cancel();
		}
		const cancelSubject = super.cancel();
		if (this.isNew) {
			this.reduxService.dispatch({ history: 'reset', type: 'CREATE_NEW' });
			this.form.reset();
		} else {
			this.overlayService.setLoading(true);
			cancelSubject.subscribe((cancelProject: Project) => {
				this.projectService.setProject(cancelProject);
				this.overlayService.setLoading(this.statsLoading || this.entityLoading);
			});
		}

		return cancelSubject;
	}

	save(force: boolean = false, extraHeaders?: any): Observable<any> {
		// console.warn('fake saving entity', JSON.parse(JSON.stringify(this.entity)));
		const p = this.entity as Project;
		if (p && p.subtype !== 'versioned') {

			if (!p.mainMediaClipId) {
				this.overlayService.createNotice({
					type: 'warning',
					icon: 'warning',
					title: this.translate.instant('NO_MAIN_MEDIACLIP_FOUND_TITLE'),
					contents: this.translate.instant('NO_MAIN_MEDIACLIP_FOUND_MESSAGE'),
				});
			} else {

				const dialog: DialogDefinition = {
					icon: 'warning',
					title: this.translate.instant('SAVING_NEW_PROJECT_STRUCTURE_TITLE'),
					contents: this.translate.instant('SAVING_NEW_PROJECT_STRUCTURE'),
					onConfirm: () => {
						let success = false;
						if (!p.flowchart || !p.flowchart.steps || !Object.keys(p.flowchart.steps).length) {
							try {
								const generatedFlowchart = this.projectService
									.autoGenerateFlowchart(this.entity as Project, this.flowchartService.getGridRules(false));
								// console.warn('generatedFlowchart', generatedFlowchart);
								if (generatedFlowchart) {
									p.flowchart = generatedFlowchart;
									success = true;
								}
							} catch (e) {
								success = false;
							}
						} else {
							success = true;
						}
						if (success) {
							p.subtype = 'versioned';
							this.reduxService.dispatch({ history: 'reset', type: 'FROM_LOAD', loadData: { data: p.flowchart } });
							const saveSubject = super.save(force, extraHeaders);
							return saveSubject;
						} else {
							this.overlayService.createNotice({
								icon: 'error',
								title: this.translate.instant('COULD_NOT_GENERATE_FLOWCHART_TITLE'),
								contents: this.translate.instant('COULD_NOT_GENERATE_FLOWCHART'),
							});
							return;
						}
					}
				};
				this.overlayService.createDialog(dialog);
			}
		} else {
			const quickEditSubject = this.quickeditService.save();
			quickEditSubject.pipe(
				tap(() => super.save(force, extraHeaders))
			)
				.subscribe();

			return quickEditSubject;
		}

		const quickEditSubjectOnly = this.quickeditService.save();
		quickEditSubjectOnly.subscribe();
		return;
	}

	editInteractivity(): void {
		// notify user when adding interactivity to a new project
		if (!this.hasTimeline()) {
			const dialog: DialogDefinition = {
				icon: 'warning',
				title: this.translate.instant('PROJECT_LEVEL_INTERACTIVITY'),
				contents: this.translate.instant('HELP.PROJECT_LEVEL_INTERACTIVITY'),
				onConfirm: () => {
					this.router.navigate(['/library/interactivity/', 'new'], { queryParams: { parentEntityType: 'Project', parentEntityId: this.entity.id } });
				},
				onCancel: () => { }
			};
			this.overlayService.createDialog(dialog);
		} else {
			this.router.navigate(['/library/interactivity/', (this.entity as Project)?.timelines[0]?.id]);
		}
	}

	getMainMediaClip() {
		// this.mainMediaClipSub = this.projectService.mainMediaClipSubject.subscribe((mediaClip: Mediaclip) => {
		// 	this.mainMediaClip = mediaClip;
		// });
	}

	load(id: string, duplicating: boolean = false, version?: SapiEntityVersion): Observable<SapiEntity> {
		const loadObservable = super.load(id, duplicating, version);
		this.overlayService.setLoading(true);

		loadObservable.subscribe((project: Project) => {
			this.overlayService.setLoading(this.statsLoading || this.entityLoading);

			this.checklistItems = [
				{ key: 'title', label: 'TITLE', checked: !!project.title },
				{
					key: 'thumbnails', label: 'THUMBNAIL', checked: (project.subtype && project.subtype === 'simple')
						|| (project.thumbnails && project.thumbnails.some((thumbnail: any) => thumbnail.main === true))
				},
				{ key: 'deeplink', label: 'DEEP_LINK', checked: !!project.deeplink },
				{ key: 'status', label: 'PUBLISHED', checked: project.status === 'published' }
			];

			const p = project;
			this.useGenerate = (!p.flowchart || !p.flowchart.steps || Object.keys(p.flowchart.steps).length === 0)
				&& p.chapters && p.chapters.length > 0;
			// overrule for now.
			this.useGenerate = false;
			p.flowchart = p.flowchart || { steps: {} };
			p.flowchart.steps = p.flowchart.steps || {};

			// }
			if (this.useGenerate) {
				this.overlayService.setLoading(true);
				// turned this off when moving to new stats backend. Isn't used at the moment anyways.
				// this.projectService.loadProjectStats(null, project).subscribe((projectWithStats: Project) => {
				// 	let flowchart = this.projectService.autoGenerateFlowchart();
				// 	// console.warn('projectWithStats', projectWithStats, 'flowchart', flowchart, 'projectStats', this.projectService.projectStats);
				// 	this.reduxService.dispatch({ history: 'reset', type: 'FROM_LOAD', loadData: { data: flowchart }});
				// 	this.overlayService.setLoading(this.entityLoading);
				// 	this.overlayService.createToast(this.translate.instant('AUTOGENERATED_FLOWCHART'), 'cogs');
				// });
			} else {
				// create FlowchartStep instances of existing data
				Object.keys(p.flowchart.steps).forEach((stepId) => {
					p.flowchart.steps[stepId] = new FlowchartStep({ ...p.flowchart.steps[stepId] });

					if (p.mainMediaClipId && p.flowchart.steps[stepId].isStart) {
						p.flowchart.steps[stepId].update({
							entityId: p.mainMediaClipId
						});
					}
				});
				// }
				if (!p.name && p.title) {
					p.name = p.title;
				}
				if (!p.thumbnails) {
					p.thumbnails = [];
				}
				// this.setDefaultMetrics(p);

				this.entity = p as ProjectSapiEntity;
				this.reduxService.dispatch({ history: 'reset', type: 'FROM_LOAD', loadData: { data: p.flowchart } });
				this.reduxService.dispatch({ history: 'reset', type: 'FROM_LOAD', loadData: { data: p.flowchart } });
				this.overlayService.setLoading(this.statsLoading || this.entityLoading);
			}
			this.projectService.setProject(p);

			if (p && p.kpiCalculations && p.kpiCalculations.length) {
				// For KPI sidebar, always show month to date
				const startDate = new Date();
				startDate.setDate(1);
				startDate.setHours(0, 0, 0, 0);

				const endDate = new Date();
				endDate.setDate(endDate.getDate() - 1);
				endDate.setHours(23, 59, 59, 999);

				const performanceParams = {
					TZ: this.timeZoneName,
					fromDate: startDate.toISOString(),
					granularity: 'month',
					toDate: endDate.toISOString(),
				};

				this.loadingPerformanceData = true;
				this.projectService.getProjectPerformanceStatsData(performanceParams).subscribe((data: any) => {
					if (data && data.items && data.items.length) {
						data.items.forEach((kpiItem: any) => {
							this.projectKpiService.setPerformanceStatsData(kpiItem);
						});
						this.loadingPerformanceData = false;
					}
				});
			}
		});
		return loadObservable;
	}

	setDefaultMetrics(project: any) {
		if (project && (!project.kpiCalculations || project.kpiCalculations.length === 0)) {
			project.kpiCalculations = [];
			project.kpiCalculations.unshift({
				label: 'Views',
				parts: [
					{
						entity: 'project',
						icon: 'paper-plane',
						label: 'Project starts',
						property: 'views',
						type: 'variable'
					}
				],
				isPercentage: false,
				isDefault: true
			});
		}
	}
}
